import React, { useState, useEffect, useCallback, useRef } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Search, Cpu, Wifi, WifiOff, Package, Building2, Upload, Loader2, Edit, History, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, FilterX, CalendarDays, SearchIcon } from 'lucide-react';
import { getDispositivos, getDispositivosFiltros, updateDispositivo, syncMasivoTo4sat, getConfig, getSyncLog, getEmpresas, scraperPatente } from '../../lib/gestion4satApi';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const PAGE_SIZES = [25, 50, 100, 500, 1000];

export default function G4sDispositivosPage() {
  const [dispositivos, setDispositivos] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [syncing, setSyncing] = useState(false);
  const [searchParams] = useSearchParams();
  const showStock = searchParams.get('stock') === '1';
  const [fontSize, setFontSize] = useState('normal');

  // Filters
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroGpsMarca, setFiltroGpsMarca] = useState('');
  const [filtroVehiculoTipo, setFiltroVehiculoTipo] = useState('');
  const [filtroVehiculoMarca, setFiltroVehiculoMarca] = useState('');
  const [filtroConectividad, setFiltroConectividad] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ gps_marca: [], vehiculo_tipo: [], vehiculo_marca: [], activity_status: [] });

  // Edit dialog
  const [editDevice, setEditDevice] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [empresaResults, setEmpresaResults] = useState([]);
  const [empresaLoading, setEmpresaLoading] = useState(false);
  const [showEmpresaDropdown, setShowEmpresaDropdown] = useState(false);
  const [scrapingPatente, setScrapingPatente] = useState(false);
  const empresaSearchTimer = useRef(null);

  // Sync log dialog
  const [showSyncLog, setShowSyncLog] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncLogLoading, setSyncLogLoading] = useState(false);

  const searchTimer = useRef(null);

  // Load filter options once
  useEffect(() => {
    getDispositivosFiltros().then(setFilterOptions).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        q: search,
        page,
        limit: pageSize,
        estado: filtroEstado && filtroEstado !== 'all' ? filtroEstado : undefined,
        sin_empresa: showStock || undefined,
        gps_marca: filtroGpsMarca && filtroGpsMarca !== 'all' ? filtroGpsMarca : undefined,
        vehiculo_tipo: filtroVehiculoTipo && filtroVehiculoTipo !== 'all' ? filtroVehiculoTipo : undefined,
        vehiculo_marca: filtroVehiculoMarca && filtroVehiculoMarca !== 'all' ? filtroVehiculoMarca : undefined,
        activity_status: filtroConectividad && filtroConectividad !== 'all' ? filtroConectividad : undefined,
        fecha_desde: filtroFechaDesde || undefined,
        fecha_hasta: filtroFechaHasta || undefined,
      };
      const [dispRes, configRes] = await Promise.all([
        getDispositivos(params),
        getConfig(),
      ]);
      setDispositivos(dispRes.dispositivos);
      setTotal(dispRes.total);
      setTotalPages(dispRes.total_pages || 1);
      setFontSize(configRes.tabla_font_size || 'normal');
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [search, page, pageSize, filtroEstado, filtroGpsMarca, filtroVehiculoTipo, filtroVehiculoMarca, filtroConectividad, filtroFechaDesde, filtroFechaHasta, showStock]);

  useEffect(() => { load(); }, [load]);

  // Reset page to 1 when filters or search change
  const resetPage = () => setPage(1);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(resetPage, 0);
  };

  const handleFilterChange = (setter) => (val) => {
    setter(val);
    setPage(1);
  };

  const handlePageSizeChange = (val) => {
    setPageSize(Number(val));
    setPage(1);
  };

  const activeFilterCount = [filtroEstado, filtroGpsMarca, filtroVehiculoTipo, filtroVehiculoMarca, filtroConectividad, filtroFechaDesde, filtroFechaHasta]
    .filter(f => f && f !== 'all').length;

  const clearAllFilters = () => {
    setFiltroEstado('');
    setFiltroGpsMarca('');
    setFiltroVehiculoTipo('');
    setFiltroVehiculoMarca('');
    setFiltroConectividad('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setPage(1);
  };

  const handleSyncMasivo = async () => {
    setSyncing(true);
    try {
      const res = await syncMasivoTo4sat();
      toast.success(res.message);
      if (res.errors > 0) toast.warning(`${res.errors} errores durante la sincronización`);
    } catch (e) { toast.error(e.message); }
    setSyncing(false);
  };

  const openEdit = (d) => {
    setEditDevice(d);
    setEditForm({
      patente: d.patente_formateada || d.patente || '',
      empresa_id: d.empresa_id || '',
      empresa_razon_social: d.empresa_razon_social || '',
      gps_marca: d.gps_marca || '',
      gps_modelo: d.gps_modelo || '',
      vehiculo_marca: d.vehiculo_marca || '',
      vehiculo_modelo: d.vehiculo_modelo || '',
      vehiculo_anio: d.vehiculo_anio || '',
      vehiculo_color: d.vehiculo_color || '',
      vehiculo_tipo: d.vehiculo_tipo || '',
      iccid: d.iccid || '',
      notas: d.notas || '',
      estado: d.estado || 'activo',
    });
    setEmpresaSearch(d.empresa_razon_social || '');
    setShowEmpresaDropdown(false);
  };

  const searchEmpresas = async (q) => {
    setEmpresaSearch(q);
    setShowEmpresaDropdown(true);
    if (empresaSearchTimer.current) clearTimeout(empresaSearchTimer.current);
    if (!q || q.length < 2) { setEmpresaResults([]); return; }
    empresaSearchTimer.current = setTimeout(async () => {
      setEmpresaLoading(true);
      try {
        const res = await getEmpresas({ q, limit: 10, estado: 'activo' });
        setEmpresaResults(res.empresas || []);
      } catch { setEmpresaResults([]); }
      setEmpresaLoading(false);
    }, 300);
  };

  const selectEmpresa = (emp) => {
    setEditForm(f => ({ ...f, empresa_id: emp.id, empresa_razon_social: emp.razon_social }));
    setEmpresaSearch(emp.razon_social);
    setShowEmpresaDropdown(false);
  };

  const clearEmpresa = () => {
    setEditForm(f => ({ ...f, empresa_id: '', empresa_razon_social: '' }));
    setEmpresaSearch('');
    setShowEmpresaDropdown(false);
  };

  const handleScrapPatente = async () => {
    const pat = editForm.patente?.trim();
    if (!pat) { toast.error('Ingresa una patente primero'); return; }
    setScrapingPatente(true);
    try {
      const res = await scraperPatente(pat);
      if (res.datos) {
        const d = res.datos;
        setEditForm(f => ({
          ...f,
          vehiculo_marca: d.marca || f.vehiculo_marca,
          vehiculo_modelo: d.modelo || f.vehiculo_modelo,
          vehiculo_anio: d.anio || f.vehiculo_anio,
          vehiculo_color: d.color || f.vehiculo_color,
          vehiculo_tipo: d.tipo || f.vehiculo_tipo,
        }));
        toast.success(`Datos obtenidos de ${res.source === 'cache' ? 'cache' : 'patentechile.com'}`);
      } else {
        toast.warning('No se encontraron datos para esta patente');
      }
    } catch (e) { toast.error(e.message || 'Error al consultar patente'); }
    setScrapingPatente(false);
  };

  const handleEditSave = async () => {
    if (!editDevice) return;
    setEditSaving(true);
    try {
      await updateDispositivo(editDevice.id, editForm);
      toast.success('Dispositivo actualizado');
      setEditDevice(null);
      load();
    } catch (e) { toast.error(e.message); }
    setEditSaving(false);
  };

  const openSyncLog = async () => {
    setShowSyncLog(true);
    setSyncLogLoading(true);
    try {
      const res = await getSyncLog(50);
      setSyncLogs(res.logs || []);
    } catch (e) { toast.error(e.message); }
    setSyncLogLoading(false);
  };

  const fontClass = fontSize === 'small' ? 'text-[11px]' : fontSize === 'large' ? 'text-sm' : 'text-xs';

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-dispositivos-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {showStock ? 'Dispositivos en Stock' : 'Dispositivos'}
          </h1>
          <p className="text-sm text-muted-foreground">{total} dispositivos {showStock ? 'sin asignar' : 'registrados'}</p>
        </div>

        {/* Search + Actions row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por IMEI, patente, empresa..."
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              className="pl-9"
              data-testid="search-dispositivos"
            />
          </div>
          <Button
            variant={showFilters ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
            data-testid="btn-toggle-filters"
          >
            <Filter className="h-4 w-4" />
            Filtros
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={openSyncLog} className="gap-2" data-testid="btn-sync-log">
            <History className="h-4 w-4" /> Log Sync
          </Button>
          <Button
            variant="outline" size="sm" onClick={handleSyncMasivo}
            disabled={syncing} className="gap-2" data-testid="btn-sync-4sat-masivo"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Sync → 4sat.cl
          </Button>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <Card className="p-4" data-testid="filters-panel">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Filtros avanzados</span>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-7 gap-1 text-xs text-muted-foreground" data-testid="btn-clear-filters">
                  <FilterX className="h-3 w-3" /> Limpiar filtros
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Estado</Label>
                <Select value={filtroEstado} onValueChange={handleFilterChange(setFiltroEstado)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filtro-estado">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="activo">Asignados</SelectItem>
                    <SelectItem value="stock">Stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Conectividad</Label>
                <Select value={filtroConectividad} onValueChange={handleFilterChange(setFiltroConectividad)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filtro-conectividad">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {filterOptions.activity_status.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">GPS Marca</Label>
                <Select value={filtroGpsMarca} onValueChange={handleFilterChange(setFiltroGpsMarca)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filtro-gps-marca">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {filterOptions.gps_marca.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Vehículo Tipo</Label>
                <Select value={filtroVehiculoTipo} onValueChange={handleFilterChange(setFiltroVehiculoTipo)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filtro-vehiculo-tipo">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {filterOptions.vehiculo_tipo.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Vehículo Marca</Label>
                <Select value={filtroVehiculoMarca} onValueChange={handleFilterChange(setFiltroVehiculoMarca)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="filtro-vehiculo-marca">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {filterOptions.vehiculo_marca.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Instalación desde</Label>
                <Input
                  type="date"
                  value={filtroFechaDesde}
                  onChange={e => { setFiltroFechaDesde(e.target.value); setPage(1); }}
                  className="h-8 text-xs"
                  data-testid="filtro-fecha-desde"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Instalación hasta</Label>
                <Input
                  type="date"
                  value={filtroFechaHasta}
                  onChange={e => { setFiltroFechaHasta(e.target.value); setPage(1); }}
                  className="h-8 text-xs"
                  data-testid="filtro-fecha-hasta"
                />
              </div>
            </div>
          </Card>
        )}

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className={`w-full ${fontClass}`}>
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-2 font-semibold">IMEI</th>
                  <th className="text-left p-2 font-semibold">Patente</th>
                  <th className="text-left p-2 font-semibold hidden md:table-cell">Empresa</th>
                  <th className="text-left p-2 font-semibold hidden lg:table-cell">GPS</th>
                  <th className="text-left p-2 font-semibold hidden lg:table-cell">Vehículo</th>
                  <th className="text-left p-2 font-semibold hidden sm:table-cell">ICCID</th>
                  <th className="text-center p-2 font-semibold">Conectividad</th>
                  <th className="text-center p-2 font-semibold">Estado</th>
                  <th className="text-center p-2 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b"><td colSpan={9} className="p-2"><Skeleton className="h-7 w-full" /></td></tr>
                  ))
                ) : dispositivos.length === 0 ? (
                  <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">
                    Sin dispositivos encontrados.
                  </td></tr>
                ) : (
                  dispositivos.map(d => (
                    <tr key={d.id} className="border-b hover:bg-muted/30">
                      <td className="p-2 font-mono">{d.imei}</td>
                      <td className="p-2 font-mono font-medium">{d.patente_formateada || '-'}</td>
                      <td className="p-2 hidden md:table-cell">
                        {d.empresa_razon_social ? (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3 text-emerald-500 shrink-0" />
                            <span className="truncate max-w-[150px]">{d.empresa_razon_social}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Sin asignar</span>
                        )}
                      </td>
                      <td className="p-2 hidden lg:table-cell">
                        {d.gps_marca && `${d.gps_marca} ${d.gps_modelo || ''}`}
                      </td>
                      <td className="p-2 hidden lg:table-cell">
                        {d.vehiculo_marca && `${d.vehiculo_marca} ${d.vehiculo_modelo || ''}`}
                        {d.vehiculo_anio && ` (${d.vehiculo_anio})`}
                      </td>
                      <td className="p-2 hidden sm:table-cell font-mono" title={d.iccid || ''}>
                        {d.iccid || <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="p-2 text-center">
                        {d.activity_status === 'Online' ? (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                            <Wifi className="h-3 w-3 mr-1" /> Online
                          </Badge>
                        ) : d.activity_status === 'Offline' ? (
                          <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
                            <WifiOff className="h-3 w-3 mr-1" /> Offline
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">N/D</span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        <Badge variant={d.estado === 'activo' ? 'default' : 'secondary'}
                          className={d.estado === 'stock' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' : ''}>
                          {d.estado === 'stock' ? <><Package className="h-3 w-3 mr-1" />Stock</> : d.estado}
                        </Badge>
                      </td>
                      <td className="p-2 text-center">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(d)} data-testid={`btn-edit-${d.imei}`}>
                          <Edit className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t" data-testid="pagination-bar">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Mostrando {total > 0 ? startItem : 0}-{endItem} de {total}</span>
              <span className="mx-1">|</span>
              <span>Por página:</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="h-7 w-20 text-xs" data-testid="select-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map(s => (
                    <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(1)} data-testid="btn-first-page">
                <ChevronsLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="btn-prev-page">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs px-2 min-w-[80px] text-center" data-testid="page-indicator">
                Pág {page} / {totalPages}
              </span>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="btn-next-page">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="btn-last-page">
                <ChevronsRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Edit Device Dialog */}
      <Dialog open={!!editDevice} onOpenChange={() => setEditDevice(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Dispositivo</DialogTitle>
            <DialogDescription>IMEI: {editDevice?.imei}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {/* Patente + Scraper */}
            <div className="col-span-2">
              <Label className="text-xs">Patente</Label>
              <div className="flex gap-2">
                <Input value={editForm.patente} onChange={e => setEditForm({...editForm, patente: e.target.value})} className="h-8 text-xs flex-1" />
                <Button
                  variant="outline" size="sm" className="h-8 gap-1 text-xs shrink-0"
                  onClick={handleScrapPatente} disabled={scrapingPatente || !editForm.patente?.trim()}
                  data-testid="btn-scraper-patente"
                  title="Buscar datos del vehículo en patentechile.com"
                >
                  {scrapingPatente ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                  Buscar datos
                </Button>
              </div>
            </div>
            {/* Empresa selector */}
            <div className="col-span-2 relative">
              <Label className="text-xs">Empresa</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    value={empresaSearch}
                    onChange={e => searchEmpresas(e.target.value)}
                    onFocus={() => { if (empresaSearch.length >= 2) setShowEmpresaDropdown(true); }}
                    placeholder="Buscar empresa..."
                    className="h-8 text-xs"
                    data-testid="input-empresa-search"
                  />
                  {showEmpresaDropdown && (empresaResults.length > 0 || empresaLoading) && (
                    <div className="absolute z-50 top-9 left-0 right-0 bg-popover border rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="empresa-dropdown">
                      {empresaLoading ? (
                        <div className="p-2 text-xs text-muted-foreground text-center">Buscando...</div>
                      ) : empresaResults.map(emp => (
                        <div
                          key={emp.id}
                          className="p-2 text-xs hover:bg-muted cursor-pointer flex justify-between"
                          onClick={() => selectEmpresa(emp)}
                        >
                          <span className="font-medium truncate">{emp.razon_social}</span>
                          <span className="text-muted-foreground ml-2 shrink-0">{emp.rut_formateado}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {editForm.empresa_id && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={clearEmpresa} title="Quitar empresa">
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {editForm.empresa_id && (
                <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                  <Building2 className="h-3 w-3" />
                  <span>{editForm.empresa_razon_social}</span>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <Select value={editForm.estado} onValueChange={v => setEditForm({...editForm, estado: v})}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="stock">Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">ICCID</Label>
              <Input value={editForm.iccid} onChange={e => setEditForm({...editForm, iccid: e.target.value})} className="h-8 text-xs font-mono" />
            </div>
            <div>
              <Label className="text-xs">GPS Marca</Label>
              <Input value={editForm.gps_marca} onChange={e => setEditForm({...editForm, gps_marca: e.target.value})} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">GPS Modelo</Label>
              <Input value={editForm.gps_modelo} onChange={e => setEditForm({...editForm, gps_modelo: e.target.value})} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Vehículo Marca</Label>
              <Input value={editForm.vehiculo_marca} onChange={e => setEditForm({...editForm, vehiculo_marca: e.target.value})} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Vehículo Modelo</Label>
              <Input value={editForm.vehiculo_modelo} onChange={e => setEditForm({...editForm, vehiculo_modelo: e.target.value})} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Año</Label>
              <Input value={editForm.vehiculo_anio} onChange={e => setEditForm({...editForm, vehiculo_anio: e.target.value})} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Color</Label>
              <Input value={editForm.vehiculo_color} onChange={e => setEditForm({...editForm, vehiculo_color: e.target.value})} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Input value={editForm.vehiculo_tipo} onChange={e => setEditForm({...editForm, vehiculo_tipo: e.target.value})} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Input value={editForm.notas} onChange={e => setEditForm({...editForm, notas: e.target.value})} className="h-8 text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDevice(null)}>Cancelar</Button>
            <Button onClick={handleEditSave} disabled={editSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-guardar-dispositivo">
              {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Log Dialog */}
      <Dialog open={showSyncLog} onOpenChange={setShowSyncLog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Historial de Sync → 4sat.cl</DialogTitle>
            <DialogDescription>Registro de datos sincronizados a la plataforma</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh]">
            {syncLogLoading ? <Skeleton className="h-32" /> : syncLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Sin registros de sync</p>
            ) : (
              <div className="space-y-3">
                {syncLogs.map(log => (
                  <div key={log.id} className="p-3 rounded-lg border text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={log.resultado === 'ok' ? 'default' : 'secondary'} className="text-[10px]">
                          {log.resultado === 'ok' ? 'OK' : log.resultado}
                        </Badge>
                        <span className="font-mono font-medium">{log.imei}</span>
                        <span className="text-muted-foreground">{log.patente}</span>
                      </div>
                      <span className="text-muted-foreground">{log.timestamp && new Date(log.timestamp).toLocaleString('es-CL')}</span>
                    </div>
                    {log.campos_actualizados && (
                      <div className="text-muted-foreground">
                        {Object.entries(log.campos_actualizados).map(([k, v]) => (
                          <span key={k} className="inline-block mr-3"><strong>{k}</strong>: {v}</span>
                        ))}
                      </div>
                    )}
                    {log.usuario && <span className="text-muted-foreground">Por: {log.usuario}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </G4sLayout>
  );
}
